/* eslint-disable @typescript-eslint/no-shadow */
import React, { useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { KDialog } from "@kyburz/kdesign-system";
import useSetState from "../../../hooks/useSetState";
import { useTableFilters } from "../../../hooks/useTableFilters";
import { useTableSorts } from "../../../hooks/useTableSorts";
import { setAlert } from "../../../redux/actions/actions";
import {
  bulkDeleteDevices,
  deleteDevice,
  downloadDevices,
  getDevices,
  importDevices,
} from "../../../apiCalls/config/devices";
import BulkUpload from "../../../components/bulkUpload/BulkUpload";
import { deviceElements } from "../../../utils/bulkUploadElements";
import SMSPopup from "./SMSPopup/SMSPopup";
import {
  cancelFirmwareUpdate,
  getFirmwareVersion,
} from "../../../apiCalls/config/firmware";
import UpdatePopup from "./UpdatePopup/UpdatePopup";
import {
  cancelConfigUpdate,
  downloadConfigs,
} from "../../../apiCalls/config/config";
import URLModal from "./URLModal/URLModal";
import FsmPopup from "./FsmPopup/FsmPopup";
import BulkUpdatePopup from "./BulkUpdatePopup/BulkUpdatePopup";
import CurrentDataPopup from "./CurrentDataPopup/CurrentDataPopup";
import KInfiniteTable from "../../../components/table/KInfiniteTable";
import { useStyles } from "./styles";
import { DeviceColumns, FsmType, UpdateType } from "./types";
import { transformFilterToId, transformIdToFilter, transformIdToSort, mapDeviceResponse } from "./utils";
import { useBillingTariffs } from "../hooks/useBillingTariffs";
import ScheduledUpdatePopup from "./ScheduledUpdatePopup/ScheduledUpdatePopup";
import { getColumns } from "./columns";
import DevicesToolbar from "./DevicesToolbar/DevicesToolbar";

const MemoizedKInfiniteTable = React.memo(
  (props: any) => (
    <KInfiniteTable
      data={props.data}
      hasNextPage={props.hasNextPage}
      isNextPageLoading={props.isNextPageLoading}
      loadNextPage={props.loadNextPage}
      columns={props.columns}
      filterTable={props.filterTable}
      loading={props.loading}
      sortTable={props.sortTable}
      manualRowSelectedKey={props.manualRowSelectedKey}
      onControlledSelect={props.onControlledSelect}
      onControlledSelectAll={props.onControlledSelectAll}
      autoResetSelectedRows={props.autoResetSelectedRows}
      hasFilter
      manualFilters
      hasStickyColumns={true}
      manualSortBy
      filterValue={props.filterValue}
      initialSortBy={props.initialSortBy}
      selectionColumnProps={{ sticky: "left" }}
    />
  ),
  (oldProps, newProps) => {
    return (
      oldProps.loading === newProps.loading && oldProps.data === newProps.data
    );
  },
);

const initialSortRule = { id: DeviceColumns.IMEI, desc: false };

const DevicesList: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { CancelToken } = axios;
  const region = localStorage.getItem("region");
  const { getTariffByLicenseNumber } = useBillingTariffs();

  const [filters, updateFilters, filterBy] = useTableFilters(
    DeviceColumns,
    transformIdToFilter,
    transformFilterToId,
  );

  const [sorts, updateSorts] = useTableSorts(initialSortRule);

  const sortBy = useMemo(() => {
    return sorts.map((x) => `${transformIdToSort(x.id, x.desc)}`).join(",");
  }, [sorts]);

  const [
    {
      query,
      pagesLoaded,
      hasNextPage,
      isNextPageLoading,
      devices,
      deleteModalOpen,
      bulkDeleteModalOpen,
      deviceToDelete,
      reload,
      cancelTokenSource,
      showBulkUploadModal,
      showBulkUpdateModal,
      loading,
      isAllSelected,
      isDownloading,
      smsModalOpen,
      smsCommand,
      smsIgnitionOn,
      fwLoading,
      updateModalOpen,
      scheduleModalOpen,
      updateType,
      urlModalOpen,
      configDownloadUrl,
      fsmModalOpen,
      fsmForce,
      fsmType,
      cancelModalOpen,
      currentDataModalOpen,
      currentDataImei,
      currentDataContent,
      selectedDeviceIds,
    },
    setState,
  ] = useSetState({
    query: "",
    pagesLoaded: 0,
    hasNextPage: true,
    isNextPageLoading: false,
    devices: [],
    deleteModalOpen: false,
    bulkDeleteModalOpen: false,
    deviceToDelete: "",
    reload: false,
    cancelTokenSource: null,
    showBulkUploadModal: false,
    showBulkUpdateModal: false,
    loading: true,
    isAllSelected: false,
    isDownloading: false,
    smsModalOpen: false,
    smsCommand: "",
    smsIgnitionOn: false,
    fwLoading: false,
    updateModalOpen: false,
    scheduleModalOpen: false,
    updateType: null,
    elementsToUpdate: [],
    urlModalOpen: false,
    configDownloadUrl: "",
    fsmModalOpen: false,
    fsmForce: false,
    fsmType: "",
    cancelModalOpen: false,
    currentDataModalOpen: false,
    currentDataImei: "",
    currentDataContent: "",
    selectedDeviceIds: [],
  });

  const buttonDisabled =
    !selectedDeviceIds.length ||
    (selectedDeviceIds[0] === "all" && filterBy === "");

  const handleChange = (e) => setState({ [e.target.name]: e.target.value });

  const handleIgnitionOn = (e) => setState({ smsIgnitionOn: e.target.checked });

  const handleReload = () => {
    setState({ reload: true });
  };

  const handleDelete = async () => {
    setState({ deleteModalOpen: false, deviceToDelete: "", query: "" });
    const res = await deleteDevice(deviceToDelete);
    if (res.data) {
      dispatch(setAlert(`Device ${deviceToDelete} deleted`, "success"));
      handleReload();
    } else {
      dispatch(setAlert(res.detail, "error"));
    }
  };

  const handleBulkDelete = async (ids: string[]) => {
    setState({ bulkDeleteModalOpen: false });
    const res = await bulkDeleteDevices(ids);
    if (res.data) {
      const { data } = res.data;
      if (data.failed > 0) {
        const alertType = data.deletions > 0 ? "warning" : "error";
        dispatch(
          setAlert(
            `${data?.deletions} devices were deleted. Failed to delete ${data.failed} devices.`,
            alertType,
          ),
        );
      } else {
        dispatch(
          setAlert(`${data?.deletions} devices were deleted`, "success"),
        );
      }
      setState({ selectedDeviceIds: [] });
      handleReload();
    } else {
      dispatch(setAlert(res.detail, "error"));
    }
  };

  const toggleDeletePopup = (e, row) =>
    setState({ deleteModalOpen: !deleteModalOpen, deviceToDelete: row.imei });

  const loadDevicesPage = useCallback(
    (query, page, startEmpty = true) => {
      if (cancelTokenSource !== null) {
        cancelTokenSource.cancel("Search cancelled");
      }
      const source = CancelToken.source();
      setState({
        isNextPageLoading: true,
        cancelTokenSource: source,
        loading: true,
      });
      getDevices(query, page, source.token, filterBy, sortBy)
        .then((res) => {
          const previousArray = startEmpty ? [] : devices;
          setState({
            devices: [...previousArray].concat(
              ...res.data?.data.map(mapDeviceResponse),
            ),
            isNextPageLoading: false,
            pagesLoaded: page,
            cancelToken: null,
            loading: false,
          });
        })
        .catch((e) => {
          setState({
            isNextPageLoading: false,
            pagesLoaded: page,
            cancelToken: null,
          });
          console.error(e);
        });
    },
    [filterBy, sortBy, devices],
  );

  const loadNextPage = useCallback(() => {
    if (hasNextPage && devices.length > 0) {
      loadDevicesPage(query, pagesLoaded + 1, false);
    }
  }, [devices, query, pagesLoaded, hasNextPage]);

  const toggleCurrentDataPopup = (imei: string = "", content: string = "") => {
    setState({
      currentDataModalOpen: !currentDataModalOpen,
      currentDataImei: imei,
      currentDataContent: content,
    });
  };

  const columns = useMemo(
    () =>
      getColumns({
        classes,
        onNavigate: history.push,
        onDeleteClick: (imei) => setState({ deleteModalOpen: true, deviceToDelete: imei }),
        onShowCurrentData: toggleCurrentDataPopup,
        getTariffByLicenseNumber,
      }),
    [getTariffByLicenseNumber],
  );

  const deletePopup = (
    <KDialog
      size="xs"
      title="Are you sure?"
      open={deleteModalOpen}
      primaryCTAonClick={handleDelete}
      primaryCTAColor="danger"
      primaryCTAText="Delete"
      secondaryCTAonClick={(e) => toggleDeletePopup(e, "")}
      secondaryCTAText="Cancel"
    >
      <p>
        Are you sure you want to delete the device <b>{deviceToDelete}</b>?
      </p>
    </KDialog>
  );

  const importBulk = (request): any => {
    return importDevices(request, region);
  };

  const deviceUploadBody = [
    "imei",
    "customer",
    "template",
    "hardware_type",
    "sim_type",
  ];

  const toggleBulkUpload = () => {
    setState({ showBulkUploadModal: !showBulkUploadModal });
    if (showBulkUploadModal) {
      handleReload();
    }
  };

  const bulkUploadPopup = (
    <BulkUpload
      open={showBulkUploadModal}
      toggleBulkUpload={toggleBulkUpload}
      elements={deviceElements}
      importFunction={importBulk}
      body={deviceUploadBody}
    />
  );

  const getSelectedDeviceIdsStr = (): string => {
    if (isAllSelected && devices.length >= 100) return "all";
    return selectedDeviceIds.join(",");
  };

  const exportDevices = () => {
    setState({ isDownloading: true });
    downloadDevices(getSelectedDeviceIdsStr(), filterBy).then(
      (response: any) => {
        const url = window.URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "devices.xlsx");
        document.body.appendChild(link);
        link.click();
        setState({ isDownloading: false });
      },
    );
  };

  const toggleSMSPopup = () => {
    setState({ smsModalOpen: !smsModalOpen });
  };

  const smsSuccess = () => {
    setState({ smsModalOpen: false, reload: true });
  };

  const onSuccess = () => {
    setState({ reload: true, loading: false });
  };

  const fsmSuccess = () => {
    setState({ reload: true, fsmModalOpen: false });
  };

  const startLoading = () => {
    setState({ loading: true });
  };

  const requestFirmware = (): void => {
    setState({ fwLoading: true });
    getFirmwareVersion(selectedDeviceIds).then((res) => {
      if (res?.data) {
        dispatch(setAlert("Started FW request successfully ", "success"));
        setState({ fwLoading: false, reload: true });
      } else {
        dispatch(setAlert("Error starting FW request", "error"));
        setState({ fwLoading: false });
      }
    });
  };

  const toggleUpdatePopup = () => {
    setState({ updateModalOpen: !updateModalOpen });
  };

  const toggleSchedulePopup = () => {
    setState({ scheduleModalOpen: !scheduleModalOpen });
  };

  const handleFsmForce = (e) => {
    setState({ fsmForce: e.target.checked });
  };

  const toggleFsmPopup = () => {
    setState({ fsmModalOpen: !fsmModalOpen });
  };

  const selectedHardwareTypes: Set<string> = useMemo(
    () => new Set(
      devices
        .filter(d => selectedDeviceIds.includes(d.imei))
        .map(d => d.hardware?.toLowerCase())
    ),
    [selectedDeviceIds, devices],
  );

  const selectedSimTypes: Set<string | undefined> = useMemo(
    () => new Set(
      devices
        .filter(d => selectedDeviceIds.includes(d.imei))
        .map(d => d.simType?.toLowerCase())
    ),
    [selectedDeviceIds, devices],
  );

  const handleUpdate = (updateType: UpdateType, schedule = false): void => {
    if (selectedHardwareTypes.size > 1) {
      dispatch(setAlert("Make sure devices with the same hardware type are selected", "warning"));
      return;
    }
    if (selectedSimTypes.size > 1) {
      dispatch(setAlert("Make sure devices with the same SIM type are selected", "warning"));
      return;
    }
    setState({ updateType });
    if(schedule) {
      toggleSchedulePopup()
    } else {
      toggleUpdatePopup();
    }
  };

  const toggleCancelPopup = () => {
    setState({ cancelModalOpen: !cancelModalOpen });
  };

  const handleCancelClick = (updateType: UpdateType): void => {
    setState({ loading: true });
    const selectedImeis = selectedDeviceIds;
    const req =
      updateType === UpdateType.config
        ? cancelConfigUpdate(selectedImeis)
        : cancelFirmwareUpdate(selectedImeis);
    req.then((res: any) => {
      if (res?.data?.status === 200) {
        setState({ loading: false, popupOpen: false });
        dispatch(setAlert("Cancelled Config Update successfully", "success"));
        handleReload();
      } else {
        setState({ loading: false });
        dispatch(
          setAlert(res.detail || "Error Canceling Config Update", "error"),
        );
        handleReload();
      }
    });
    toggleCancelPopup();
  };

  const handleFsmClick = (fsmType: FsmType): void => {
    setState({ fsmType });
    toggleFsmPopup();
  };

  const toggleBulkDeletePopup = () =>
    setState({ bulkDeleteModalOpen: !bulkDeleteModalOpen });

  const handleCloseUrlPopup = () => {
    setState({ urlModalOpen: false, configDownloadUrl: "" });
  };

  const startConfigDownload = () => {
    dispatch(setAlert("Initiating config download...", "warning"));
    downloadConfigs(selectedDeviceIds).then((res) => {
      if (res.data) {
        let all_ok = true;
        const warnings: string[] = [];
        Object.entries(res.data).forEach(([imei, msg]) => {
          if (msg !== "ok") {
            all_ok = false;
          }
          warnings.push(`${imei}: ${msg}`);
        });
        if (all_ok) {
          dispatch(
            setAlert("Successfully initiated config download", "success"),
          );
        } else {
          dispatch(
            setAlert(
              `Initiated config download with errors: ${warnings.join(", ")}`,
              "warning",
            ),
          );
        }
      } else {
        const error = "Could not initiate config download";
        dispatch(setAlert(error, "error"));
      }
    });
  };

  const onControlledSelectAll = (data, checked) => {
    setState({
      isAllSelected: checked,
      selectedDeviceIds: checked ? data.map((item) => item?.imei) : [],
    });
  };

  const onControlledSelect = (row, checked) => {
    const selectedDeviceIdsCopy = [...selectedDeviceIds];
    const deviceId = row.original.imei;
    let isAll = isAllSelected;
    if (checked) {
      selectedDeviceIdsCopy.push(deviceId);
    } else {
      const index = selectedDeviceIdsCopy.indexOf(deviceId);
      selectedDeviceIdsCopy.splice(index, 1);
      isAll = false;
    }
    setState({
      selectedDeviceIds: selectedDeviceIdsCopy,
      isAllSelected: isAll,
    });
  };

  const handledItems = React.useMemo(
    () =>
      devices.map((item) => ({
        ...item,
        selected: isAllSelected || selectedDeviceIds.includes(item?.imei),
      })),
    [devices, selectedDeviceIds],
  );

  const toggleBulkUpdate = () => {
    setState({ showBulkUpdateModal: !showBulkUpdateModal });
    if (showBulkUpdateModal) {
      handleReload();
    }
  };

  const bulkUpdatePopup = (
    <BulkUpdatePopup
      deviceImeis={selectedDeviceIds}
      open={showBulkUpdateModal}
      togglePopup={toggleBulkUpdate}
    />
  );

  const urlPopup = (
    <URLModal
      open={urlModalOpen}
      url={configDownloadUrl}
      closePopup={handleCloseUrlPopup}
    />
  );

  const bulkDeletePopup = useMemo(() => {
    return (
      <KDialog
        size="xs"
        title="Are you sure?"
        open={bulkDeleteModalOpen}
        primaryCTAonClick={() => handleBulkDelete(selectedDeviceIds)}
        primaryCTAColor="danger"
        primaryCTAText="Delete"
        secondaryCTAonClick={() => toggleBulkDeletePopup()}
        secondaryCTAText="Cancel"
      >
        <p>
          {`Are you sure you want to delete ${selectedDeviceIds.length} devices?`}
        </p>
      </KDialog>
    );
  }, [selectedDeviceIds, bulkDeleteModalOpen]);

  useEffect(() => {
    if (reload) {
      setState({ reload: false });
      loadDevicesPage(query, 1, true);
    }
  }, [reload]);

  useEffect(() => {
    loadDevicesPage(query, 1, true);
  }, [query, filterBy, sortBy]);



  useEffect(() => {
    if (isAllSelected) {
      setState({
        selectedDeviceIds:
          devices.length < selectedDeviceIds.length
            ? selectedDeviceIds.filter((imei) =>
                devices.some((item) => item.imei === imei),
              )
            : devices.map((item) => item.imei),
      });
    } else {
      setState({
        selectedDeviceIds: selectedDeviceIds.filter((imei) =>
          devices.some((item) => item.imei === imei),
        ),
      });
    }
  }, [devices, isAllSelected]);

  return (
    <div className={classes.container}>
      <DevicesToolbar
        loading={loading}
        isAllSelected={isAllSelected}
        selectedCount={selectedDeviceIds.length}
        totalLoaded={devices?.length}
        isDownloading={isDownloading}
        buttonDisabled={buttonDisabled}
        fwLoading={fwLoading}
        onReload={() => setState({ reload: true })}
        onExport={exportDevices}
        onScheduleFWUpdate={() => handleUpdate(UpdateType.firmware, true)}
        onFWUpdate={() => handleUpdate(UpdateType.firmware)}
        onScheduleConfigUpdate={() => handleUpdate(UpdateType.config, true)}
        onConfigUpdate={() => handleUpdate(UpdateType.config)}
        onBulkUpdate={toggleBulkUpdate}
        onSMSCommand={toggleSMSPopup}
        onNewDevice={() => history.push("/config/devices/create")}
        onImportCSV={toggleBulkUpload}
        onRequestFW={requestFirmware}
        onDownloadConfig={startConfigDownload}
        onCancelConfig={() => handleCancelClick(UpdateType.config)}
        onCancelFW={() => handleCancelClick(UpdateType.firmware)}
        onCancelFsm={() => handleFsmClick(FsmType.cancel)}
        onResetFsm={() => handleFsmClick(FsmType.reset)}
        onBulkDelete={toggleBulkDeletePopup}
      />
      <div className={classes.tableContainer}>
        <MemoizedKInfiniteTable
          data={handledItems}
          hasNextPage={hasNextPage}
          isNextPageLoading={isNextPageLoading}
          loadNextPage={loadNextPage}
          columns={columns}
          filterTable={updateFilters}
          loading={loading}
          initialSortBy={sorts}
          sortTable={updateSorts}
          manualRowSelectedKey="selected"
          onControlledSelect={onControlledSelect}
          onControlledSelectAll={onControlledSelectAll}
          autoResetSelectedRows={true}
          filterValue={filters}
        />
      </div>
      {deletePopup}
      {bulkDeletePopup}
      {bulkUploadPopup}
      {bulkUpdatePopup}
      {urlPopup}
      <SMSPopup
        selectedImeis={selectedDeviceIds}
        smsCommand={smsCommand}
        smsModalOpen={smsModalOpen}
        toggleSMSPopup={toggleSMSPopup}
        handleChange={handleChange}
        smsSuccess={smsSuccess}
        handleIgnitionOn={handleIgnitionOn}
        ignitionOn={smsIgnitionOn}
      />
      {scheduleModalOpen &&
        <ScheduledUpdatePopup
          data={{
            deviceIds: selectedDeviceIds,
            updateType,
            simTypes: selectedSimTypes,
            hardwareTypes: selectedHardwareTypes,
            scheduledUpdateID: undefined,
            deviceFirmwareID: undefined,
            allowedFrom: undefined,
            allowedTo: undefined,
            ignitionOn: false,
            ignitionOff: false,
          }}
          toggleSchedulePopup={toggleSchedulePopup}
          onSuccess={onSuccess}
          startLoading={startLoading}
        />
      }
      {updateModalOpen &&
        <UpdatePopup
          updateModalOpen={updateModalOpen}
          selectedImeis={selectedDeviceIds}
          toggleUpdatePopup={toggleUpdatePopup}
          selectedHardwareTypes={selectedHardwareTypes}
          selectedSimTypes={selectedSimTypes}
          onSuccess={onSuccess}
          startLoading={startLoading}
          updateType={updateType}
        />
      }
      <FsmPopup
        fsmModalOpen={fsmModalOpen}
        selectedImeis={selectedDeviceIds}
        toggleFsmPopup={toggleFsmPopup}
        loading={loading}
        handleFsmForce={handleFsmForce}
        fsmSuccess={fsmSuccess}
        startLoading={startLoading}
        force={fsmForce}
        fsmType={fsmType}
      />
      <CurrentDataPopup
        deviceImei={currentDataImei}
        content={currentDataContent}
        open={currentDataModalOpen}
        togglePopup={toggleCurrentDataPopup}
      />
    </div>
  );
};

export default DevicesList;
